import React, { useRef, useState, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, LOCALE_ALIASES, saveLocale, changeLocale } from '../i18n';
import { SUPPORTED_SEO_LOCALES, SeoLocaleKey, stripLocalePrefix } from '../component/seo/constants';
import { LocaleSelectorMessages } from './LocaleSelector.messages';
import './Header.css';

/**
 * The only language switcher.
 *
 * The header used to carry its own inline copy of this menu. That copy changed the
 * language but never touched the URL, and because LocaleLayout makes the URL
 * authoritative for the locale on public routes, every selection made from the
 * header was immediately reverted to English. Signed-in routes are not wrapped in
 * LocaleLayout, which is why the same menu appeared to work there — the two trees
 * disagreed rather than the menu being intermittent. One implementation, used in
 * both places, is what stops that drifting apart again.
 *
 * Choosing a language is the product's own headline capability, and this control was
 * the least usable thing on the site by keyboard or screen reader. Fixed here:
 *
 *   - Space typed a selection instead of a space, so "English (UK)" and "Chinese
 *     (Simplified)" could not be searched by name at all, and a stray Space committed
 *     a language change. Only Enter commits now.
 *   - The trigger exposed no expanded state, and its aria-label ("Language")
 *     replaced the visible text ("English (UK)") rather than including it.
 *   - The keyboard position was invisible: no aria-activedescendant, and a highlight
 *     drawn at 1.05:1 against the menu. Arrowing through 56 options told you nothing.
 *   - role="listbox" wrapped a div and a text input, which are not permitted
 *     children, so the options' relationship to it was undefined.
 *   - All 56 options were in the tab order, and closing the menu dropped focus to
 *     <body>, returning the user to the top of the document.
 *
 * The shape is the standard combobox-with-listbox pattern: the input is the
 * combobox, the options live in a sibling listbox it points at, and the input keeps
 * focus throughout while aria-activedescendant names the option in play.
 */

function findSeoLocaleKey(i18nKey: string): SeoLocaleKey | null {
  const entry = (Object.entries(SUPPORTED_SEO_LOCALES) as [SeoLocaleKey, any][])
    .find(([, v]) => v.i18nKey === i18nKey);
  return entry ? entry[0] : null;
}

interface LocaleSelectorProps {
  /** Open the menu upwards. The footer instance sits at the bottom of the page. */
  dropUp?: boolean;
  /** Called when the menu opens. The header uses it to close the mobile nav. */
  onOpen?: () => void;
}

export const LocaleSelector: React.FC<LocaleSelectorProps> = ({ dropUp = false, onOpen }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  // Tracked in state rather than read during render: reading window.matchMedia in
  // the render body makes the output depend on the viewport, which is unknowable
  // when the page is prerendered. The effect settles it immediately after mount,
  // well before the menu it gates can be opened.
  const [isMobile, setIsMobile] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  /*
   * Ids have to be unique per instance: the header and the footer both render one,
   * and aria-controls / aria-activedescendant pointing at a duplicated id would make
   * the relationship ambiguous.
   */
  const instanceId = useId();
  const listboxId = `locale-listbox-${instanceId}`;
  const optionId = (code: string) => `locale-option-${instanceId}-${code}`;

  // Both refs are consulted because on mobile the menu is portalled to
  // document.body, so it is not a descendant of the trigger.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || wrapperRef.current?.contains(target)) return;
      setOpen(false);
      setFilter('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // 768px matches the .header-mobile-toggle media query in Header.css.
  useEffect(() => {
    const query = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const filtered = Object.entries(SUPPORTED_LOCALES)
    .filter(([code, label]) => label.toLowerCase().includes(filter.toLowerCase()) || code.toLowerCase().includes(filter.toLowerCase()) || (LOCALE_ALIASES[code] || '').toLowerCase().includes(filter.toLowerCase()));

  /**
   * Closes the menu and puts focus back where it came from.
   *
   * Returning focus is the part that was missing: both Escape and choosing an option
   * left `document.activeElement` as <body>, so the user was silently returned to the
   * top of the document and had to tab through the whole header again.
   */
  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setFilter('');
    setHighlight(0);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  /*
   * Keep the highlighted option in view. With 56 options the list scrolls, so
   * arrowing past the visible window otherwise moves an indicator nobody can see.
   */
  useEffect(() => {
    if (!open) return;
    const entry = filtered[highlight];
    if (!entry) return;
    listboxRef.current
      ?.querySelector(`#${CSS.escape(optionId(entry[0]))}`)
      ?.scrollIntoView({ block: 'nearest' });
    // optionId is derived from instanceId, which is stable for the component's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, highlight, filter]);

  const selectLocale = (code: string) => {
    // changeLocale, not i18n.changeLanguage: catalogues load on demand, and switching
    // the language before its messages exist would flash the page through English.
    void changeLocale(code);
    saveLocale(code);
    close();

    // Locales that have a URL must move to it, or LocaleLayout — which treats the
    // URL as authoritative — will switch the page straight back.
    const seoKey = findSeoLocaleKey(code);
    const cleanPath = stripLocalePrefix(location.pathname);
    if (seoKey !== null && seoKey !== 'en') {
      const prefix = SUPPORTED_SEO_LOCALES[seoKey].urlPrefix;
      navigate(cleanPath === '/' ? prefix : `${prefix}${cleanPath}`);
    } else if (location.pathname !== cleanPath) {
      // Leaving a localized URL for one that has no prefix: drop the prefix, or the
      // old locale in the path would win over the choice just made.
      navigate(cleanPath);
    }
  };

  const currentLabel = SUPPORTED_LOCALES[i18n.language] || 'EN';
  const activeEntry = filtered[highlight];

  const menu = (
    <div
      ref={menuRef}
      className={`header-dropdown header-lang-dropdown${dropUp ? ' header-lang-dropdown--up' : ''}`}
    >
      <div style={{ padding: '4px 4px 6px' }}>
        <input
          type="text"
          className="header-lang-search"
          placeholder={t(LocaleSelectorMessages.SEARCH_PLACEHOLDER)}
          value={filter}
          /*
           * The combobox itself. It holds focus for as long as the menu is open, and
           * aria-activedescendant — not focus — says which option is in play, which is
           * what lets typing and arrowing coexist.
           */
          role="combobox"
          aria-expanded
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeEntry ? optionId(activeEntry[0]) : undefined}
          aria-label={t(LocaleSelectorMessages.LABEL)}
          onChange={e => { setFilter(e.target.value); setHighlight(0); }}
          onKeyDown={e => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(i => Math.min(i + 1, filtered.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(i => Math.max(i - 1, 0)); }
            else if (e.key === 'Home') { e.preventDefault(); setHighlight(0); }
            else if (e.key === 'End') { e.preventDefault(); setHighlight(Math.max(filtered.length - 1, 0)); }
            // Enter only. Space used to commit too, which made every multi-word
            // language name unsearchable and turned a stray keystroke into a
            // language change.
            else if (e.key === 'Enter') {
              if (activeEntry) { e.preventDefault(); selectLocale(activeEntry[0]); }
            }
            else if (e.key === 'Escape') { e.preventDefault(); close(); }
            // Tab leaves the control rather than walking 56 options.
            else if (e.key === 'Tab') { close(false); }
          }}
          autoFocus
        />
      </div>
      <div ref={listboxRef} role="listbox" id={listboxId} aria-label={t(LocaleSelectorMessages.LABEL)}>
        {filtered.map(([code, label], i) => (
          <button
            key={code}
            id={optionId(code)}
            className={`header-dropdown-item${i === highlight ? ' highlighted' : ''}`}
            onClick={() => selectLocale(code)}
            onMouseEnter={() => setHighlight(i)}
            role="option"
            /* The chosen language, not the keyboard position — that is activedescendant. */
            aria-selected={code === i18n.language}
            /* Options in a listbox are not tab stops; the combobox owns focus. */
            tabIndex={-1}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        className="header-nav-link"
        onClick={() => {
          const next = !open;
          if (next) { setOpen(true); setFilter(''); setHighlight(0); onOpen?.(); }
          else close();
        }}
        /*
         * The accessible name includes the current language rather than replacing it.
         * It was just "Language", which hid the visible "English (UK)" from anyone
         * relying on the name — and left the control's value unannounced.
         */
        aria-label={`${t(LocaleSelectorMessages.LABEL)}: ${currentLabel}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
        <span className="header-lang-label">{currentLabel}</span>
      </button>
      {open && (isMobile ? createPortal(menu, document.body) : menu)}
    </div>
  );
};
